import { getRouterService } from '@/core/container/ioc'
import { ref, watch, type Ref, computed } from '@/composition/vue/compositionApi'
import { useProvideInject } from '@/composition/provideInject'

export type SidebarExpandItemProps = {
  alwaysExpandable: boolean
  expandedItem: Ref<string | undefined>
  toggleItem: (itemName: string) => void
}

const sidebarExpandItemSymbol = Symbol('sidebar-expanded-menu-item')
const useSidebarExpandItemProvideInject = () => useProvideInject<SidebarExpandItemProps>(sidebarExpandItemSymbol)

export const useSidebarExpandItemProvide = (alwaysExpandable = false) => {
  const expandedItem = ref<string>()

  const toggleItem = (itemName: string) => {
    expandedItem.value = expandedItem.value === itemName ? undefined : itemName
  }

  useSidebarExpandItemProvideInject().provide({
    alwaysExpandable,
    expandedItem,
    toggleItem,
  })
}

export const useSidebarExpandItemInject = (routeName: string) => {
  const routerService = getRouterService()

  const props = useSidebarExpandItemProvideInject().inject()

  const expanded = computed(() => routeName === props?.expandedItem.value)
  const toggle = () => props?.toggleItem(routeName)

  const expandItemByRouter = () => {
    if (!props) return
    const routeMatched = !!routerService.currentRoute.value.matched.find((route) => route.name === routeName)
    if (!routeMatched) return
    props.expandedItem.value = routeName
  }
  watch(routerService.currentRoute, expandItemByRouter, { immediate: true })

  return {
    expanded,
    toggle,
  }
}

export const isSidebarAlwaysExpandable = () => {
  const props = useSidebarExpandItemProvideInject().inject()

  return {
    alwaysExpandable: props?.alwaysExpandable ?? false,
  }
}
